import axios from 'axios';
import { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { ContentCard } from '../../components/ContentCard/ContentCard';
import { ErrorMessage } from '../../components/ErrorMessage/ErrorMessage';
import { AddressSummary } from '../../components/Form/AddressSummary';
import Checkbox from '../../components/Form/Checkbox/Checkbox';
import { DeliveryInput } from '../../components/Form/DeliveryInput';
import { Layout } from '../../components/Layout/Layout';
import { Main } from '../../components/Main/Main';
import { EditBillingAddressModal } from '../../components/editOrder/EditBillingAddressModal';
import { EditDeliveryAddressModal } from '../../components/editOrder/EditDeliveryAddressModal';
import { EditOrderFooter } from '../../components/editOrder/EditOrderFooter';
import { EditOrderTotalsTable } from '../../components/editOrder/EditOrderTotalsTable';
import { AddProductModal } from '../../components/editOrder/addProduct/AddProductModal';
import { renderTemplate } from '../../components/utils';
import { useConfig } from '../../config';
import { useDebounce } from '../../hooks/useDebounce';
import { useImmer } from '../../hooks/useImmer';
import { useInterval } from '../../hooks/useInterval';
import { useTranslation } from '../../i18n/i18n';
import { Res, useApi } from '../../state/api';
import { ApiOrder, ApiOrderItem, ApiTotals, DeliveryType } from '../../types';
import { AlertMessages, useAlerts } from '../AlertMessages/AlertMessages';
import { Routing } from '../Routes';
import { EditOrderItem } from '../editOrder/EditOrderItem';
import { getSecondsLeftToEdit, getTimeLeftString, hasTimeLeft } from '../editOrder/utils';
import './EditOrderPage.css';
import { LoaderPage } from './LoaderPage';
import { cookieService } from '../cookieService';

export interface Errors {
	[key: string]: { valid: boolean; message?: string };
}

export type AddressFields =
	| 'company'
	| 'street'
	| 'zip'
	| 'city'
	| 'firstname'
	| 'lastname'
	| 'careOf'
	| 'city'
	| 'company'
	| 'email'
	| 'street'
	| 'zip'
	| 'phone';

export type ApiEditOrderPayload = ApiOrder;

interface ApiEditedOrder {
	success: boolean;
}

export const EditOrder = (): JSX.Element => {
	const { api, store, locale } = useConfig();
	type UrlParams = { orderId: string };
	const { orderId } = useParams<UrlParams>();
	const t = {
		...useTranslation().editOrder,
		orderPages: useTranslation().orderPages,
		urls: useTranslation().routes,
		footer: useTranslation().footer,
		cart: useTranslation().cart,
		form: useTranslation().form,
		generic: useTranslation().generic,
	};
	const [updateTotals, setUpdateTotals] = useState(0);
	const [totalsLoading, setTotalsLoading] = useState(false);
	const history = useHistory();
	const [loading, setLoading] = useState(false);
	const [totalsErrorMessages, setTotalsErrorMessages] = useState('');
	const { data } = useApi(Res.order, { orderId });
	const [order, setOrder] = useImmer<ApiOrder | undefined | null>(null);
	const [homeDeliveryEnabled, setHomeDeliveryEnabled] = useState(false);
	const upsertUpdateTotals = () => setUpdateTotals(new Date().getTime());
	const debouncedUpdateTotals: number = useDebounce<number>(updateTotals, 500);
	const alerts = useAlerts();
	const [timeLeft, setTimeLeft] = useState('');
	let customerType = cookieService.read('customer-type') as '' | 'company' | 'private';
	if (!customerType) customerType = '';

	useEffect(() => {
		let timer = setInterval(() => {
			if (order && !!!order.subscriptionOrder) {
				setTimeLeft(getTimeLeftString(order, t.orderPages, locale));
			}
		}, 1000);
		return () => {
			clearInterval(timer);
		};
	}, [order]);

	async function updateTotalsCall(): Promise<void> {
		setTotalsLoading(true);

		try {
			const result = await axios.post(`${api}me/orders/${orderId}/calculate-totals.json`, order, {
				withCredentials: true,
			});
			const totals = result.data as ApiTotals;

			if (order) setOrder({ ...order, totals });

			setTotalsErrorMessages('');
		} catch (error: any) {
			const errorMessages = error?.response?.data?.errors;
			if (errorMessages) {
				setTotalsErrorMessages(
					errorMessages
						.map((msg: { code: string; message: string }) => `${msg.code}: ${msg.message}`)
						.join(', ')
				);
			}
		} finally {
			setTotalsLoading(false);
		}
	}

	const onAddProduct = (item: ApiOrderItem): void => {
		const productAlreadyAdded = order?.items.find((newItem) => newItem.product.id === item.product.id);
		let quantity = item.quantity;

		if (productAlreadyAdded) quantity += productAlreadyAdded.quantity;

		setOrder((draft) => {
			if (productAlreadyAdded) {
				const updateItem = draft?.items.find((i) => i.product.id === item.product.id);

				if (updateItem) {
					updateItem.quantity = quantity;
				}
			} else {
				draft?.items.push(item);
			}
		});
		upsertUpdateTotals();
	};

	const onChangeOrderItem = (value: number, i: number) => {
		setOrder((draft) => {
			if (draft) {
				upsertUpdateTotals();
				draft.items[i].quantity = value ? value : 0;
			}
		});
	};

	useMemo(() => {
		if ((store === 'kgse' || store === 'kgdk') && order && order.items?.length > 0) {
			const cartHasNonEmoDeliveryVendor = !!order.items.some(
				(i) =>
					(i.product.vendorId !== 25 && store === 'kgse') || (i.product.vendorId !== 22 && store === 'kgdk')
			);
			if (homeDeliveryEnabled !== !cartHasNonEmoDeliveryVendor)
				setHomeDeliveryEnabled(!cartHasNonEmoDeliveryVendor);
		}
	}, [order?.items]);

	useEffect(() => {
		setOrder(data);
		if (data && !canEditOrder(data)) {
			history.push({
				pathname: t.urls.yourOrders,
				state: { editForbidden: true },
			});
		}
	}, [data]);

	useEffect(() => {
		if (debouncedUpdateTotals) {
			updateTotalsCall();
		}
	}, [debouncedUpdateTotals]);

	useInterval(
		() =>
			axios
				.get(`${api}me/orders/${orderId}/edit-session.json`, { withCredentials: true })
				// a keepalive every ten seconds, for as long as the page is open: a lost one is not
				// worth a word to the customer, but it cannot go unhandled either
				.catch(() => undefined),
		10000
	);

	const canEditOrder = (order: ApiOrder) =>
		order.status === 1 && order.editable && hasTimeLeft(getSecondsLeftToEdit(order), order.subscriptionOrder);

	const saveOrder = async () => {
		try {
			setLoading(true);
			await axios.post(`${api}me/orders/${orderId}/update.json`, order, {
				withCredentials: true,
			});
			setLoading(false);
			history.push({
				pathname: `${t.urls.yourOrders}/${orderId}`,
				state: { updated: true },
			});
		} catch (error: any) {
			// a network failure carries no response, and the alert then had nothing to say
			alerts.set({ type: 'error', message: error?.response?.data?.message ?? t.generic.somethingWentWrong });
			setLoading(false);
		}
	};

	const removeItem = (id: number, i: number) => {
		setOrder((draft) => {
			if (draft) {
				const filteredItems = draft?.items.filter((item, index) => item.id !== id || i !== index);
				draft.items = filteredItems;
			}
		});
		upsertUpdateTotals();
	};

	const onChangeShippingType = (e: { type: DeliveryType; notification: '' | 'call' | 'confer' }) => {
		upsertUpdateTotals();
		setOrder((draft) => {
			if (draft) {
				draft.shippingType = e.type;
				draft.shippingAddon = e.notification;
			}
		});
	};

	const updateBillingAddress = (address: ApiOrder['billing']) => {
		axios
			.post<ApiEditedOrder>(`${api}me/orders/${orderId}/update-billing-address.json`, address, {
				withCredentials: true,
			})
			// a background sync of state the ui has already applied; the rejection has to be taken
			.catch(() => undefined);

		setOrder((draft) => {
			if (draft) draft.billing = address;
		});
	};

	const updateDeliveryAddress = (address: ApiOrder['delivery']) => {
		axios
			.post<ApiEditedOrder>(`${api}me/orders/${orderId}/update-delivery-address.json`, address, {
				withCredentials: true,
			})
			// a background sync of state the ui has already applied; the rejection has to be taken
			.catch(() => undefined);

		setOrder((draft) => {
			if (draft) draft.delivery = address;
		});
	};

	if (!order) return <LoaderPage />;

	return (
		<Main>
			<div className="edit-order-page">
				<h1 className="private-page-heading">{renderTemplate(t.editOrderTitle, { orderId })}</h1>

				{!!!order.subscriptionOrder && !!timeLeft && getSecondsLeftToEdit(order) < 60 * 60 && (
					<ContentCard className="warning">
						{timeLeft && renderTemplate(t.orderPages.timeLeftToEdit, { timeLeft })}
					</ContentCard>
				)}
				<>
					<Layout type="two-pane">
						<ContentCard>
							<div className="order-address-header">
								<h2>{t.orderPages.deliveryAddress}</h2>
								<EditDeliveryAddressModal
									invoiceAddress={order.delivery}
									setInvoiceAddress={updateDeliveryAddress}
								/>
							</div>
							<AddressSummary address={order.delivery} />
						</ContentCard>
						<ContentCard>
							<div className="order-address-header">
								<h2>{t.orderPages.invoiceAddress}</h2>
								<EditBillingAddressModal
									billingAddress={order.billing}
									setBillingAddress={updateBillingAddress}
								/>
							</div>
							<AddressSummary address={order.billing} />
							<Checkbox
								onChange={(e) => {
									setOrder({ ...order, letterInvoice: e.target.checked });
									upsertUpdateTotals();
								}}
								label={t.orderPages.requestLetterInvoice}
								id="letterInvoice"
							></Checkbox>
						</ContentCard>
					</Layout>

					<ContentCard title={t.orderPages.deliveryMethod}>
						<DeliveryInput
							model={{
								type: order.shippingType === 'door' ? 'door_delivery' : order.shippingType,
								notification: order.shippingAddon,
							}}
							homeDeliveryEnabled={homeDeliveryEnabled}
							onChange={onChangeShippingType}
							customerType={customerType}
						/>
					</ContentCard>

					<ContentCard title={t.footer.products} className="edit-order-products">
						<div className="not-mobile">
							<edit-order-item-heading>
								<span>{t.cart.product}</span>
								<span className="text-center">{t.cart.amount}</span>
								<span className="text-right">{t.cart.total}</span>
							</edit-order-item-heading>
						</div>
						<edit-order-items>
							{order.items.map((item, i) => (
								<EditOrderItem
									index={i}
									onDelete={removeItem}
									onChange={onChangeOrderItem}
									key={item.product.id}
									item={item}
								/>
							))}
						</edit-order-items>
						<AddProductModal onSave={onAddProduct} title={renderTemplate(t.addProductTitle, { orderId })} />
						<EditOrderTotalsTable order={order} totalsLoading={totalsLoading} />

						{totalsErrorMessages ? <ErrorMessage message={totalsErrorMessages} /> : ''}
						<AlertMessages alerts={alerts} />
						<EditOrderFooter loading={loading} disabled={totalsLoading || loading} submit={saveOrder} />
					</ContentCard>
				</>
			</div>
		</Main>
	);
};

export const editOrderRouting: Routing = {
	path(urls) {
		return `${urls.yourOrders}/:orderId${urls.edit}`;
	},
	component: EditOrder,
	requiresAuth: true,
};
